/**
 * Crime analytics from approved reports: headline numbers vs the period before, weekly trend, offenses,
 * day-of-week × hour, hot spots on the map. One date-range control scopes everything.
 */
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CATEGORY_LABELS } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { SlippyMap } from '../shared/SlippyMap.tsx';
import { ErrorBox, Spinner, useLoad, PageHeader } from '../shared/ui.tsx';

const BLUE = '#2a78d6';
// Sequential blue ramp (100 → 700); empty cells use the surface tint.
const RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hourLabel = (h: number) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);
const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

/** One floating readout for the page. */
function useTip() {
  const [tip, setTip] = useState<{ x: number; y: number; lines: [string, string][] } | null>(null);
  const el = tip && (
    <div className="ca-tip" style={{ left: tip.x + 14, top: tip.y + 14 }} role="status">
      {tip.lines.map(([v, l], i) => (
        <div key={i}>
          <strong>{v}</strong> <span>{l}</span>
        </div>
      ))}
    </div>
  );
  return { show: (e: { clientX: number; clientY: number }, lines: [string, string][]) => setTip({ x: e.clientX, y: e.clientY, lines }), hide: () => setTip(null), el };
}

function Stat({ n, label, sub }: { n: ReactNode; label: string; sub?: ReactNode }) {
  return (
    <div className="ca-stat">
      <div className="ca-l">{label}</div>
      <div className="ca-n">{n}</div>
      {sub && <div className="ca-sub">{sub}</div>}
    </div>
  );
}

function Trend({ weeks, tip }: { weeks: { weekStart: string; count: number }[]; tip: ReturnType<typeof useTip> }) {
  // Drawn at the box's real width so text stays its normal size on any screen.
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(280, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 200, L = 32, R = 12, T = 12, B = 26;
  const max = Math.max(4, ...weeks.map((w) => w.count));
  const nice = Math.ceil(max / 4) * 4;
  const x = (i: number) => L + (weeks.length <= 1 ? 0 : (i * (W - L - R)) / (weeks.length - 1));
  const y = (v: number) => T + (H - T - B) * (1 - v / nice);
  const [hover, setHover] = useState<number | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const fmt = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const step = Math.max(1, Math.ceil(weeks.length / Math.max(2, Math.floor(W / 110))));
  // The current week isn't over yet: draw it dashed so a half-finished week doesn't read as a drop.
  const last = weeks.at(-1);
  const partial = !!last && Date.now() < +new Date(`${last.weekStart}T00:00:00Z`) + 7 * 86_400_000;
  const done = partial && weeks.length > 1 ? weeks.slice(0, -1) : weeks;
  return (
    <div ref={box}>
    <svg
      ref={svg}
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      className="ca-svg"
      role="img"
      aria-label={`Crimes per week: ${weeks.map((w) => `${fmt(w.weekStart)} ${w.count}`).join(', ')}`}
      onPointerMove={(e) => {
        const r = svg.current!.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * W;
        const i = Math.max(0, Math.min(weeks.length - 1, Math.round(((px - L) / (W - L - R)) * (weeks.length - 1))));
        setHover(i);
        tip.show(e, [[String(weeks[i]!.count), `crimes, week of ${fmt(weeks[i]!.weekStart)}${partial && i === weeks.length - 1 ? ' (so far)' : ''}`]]);
      }}
      onPointerLeave={() => (setHover(null), tip.hide())}
    >
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line x1={L} x2={W - R} y1={y(nice * f)} y2={y(nice * f)} className="ca-grid" />
          <text x={L - 6} y={y(nice * f) + 4} className="ca-axis" textAnchor="end">
            {Math.round(nice * f)}
          </text>
        </g>
      ))}
      {weeks.map((w, i) =>
        i % step === 0 ? (
          <text key={w.weekStart} x={x(i)} y={H - 6} className="ca-axis" textAnchor="middle">
            {fmt(w.weekStart)}
          </text>
        ) : null,
      )}
      <polygon fill={BLUE} opacity={0.08} points={`${x(0)},${y(0)} ${done.map((w, i) => `${x(i)},${y(w.count)}`).join(' ')} ${x(done.length - 1)},${y(0)}`} />
      <polyline fill="none" stroke={BLUE} strokeWidth={2} strokeLinejoin="round" points={done.map((w, i) => `${x(i)},${y(w.count)}`).join(' ')} />
      {partial && weeks.length > 1 && (
        <>
          <line x1={x(weeks.length - 2)} y1={y(weeks.at(-2)!.count)} x2={x(weeks.length - 1)} y2={y(weeks.at(-1)!.count)} stroke={BLUE} strokeWidth={2} strokeDasharray="4 4" opacity={0.6} />
          <circle cx={x(weeks.length - 1)} cy={y(weeks.at(-1)!.count)} r={3.5} fill="var(--panel)" stroke={BLUE} strokeWidth={2} opacity={0.8} />
        </>
      )}
      {done.map((w, i) => (
        <circle key={w.weekStart} cx={x(i)} cy={y(w.count)} r={2.5} fill={BLUE} />
      ))}
      {hover != null && (
        <>
          <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} className="ca-cross" />
          <circle cx={x(hover)} cy={y(weeks[hover]!.count)} r={4.5} fill={BLUE} stroke="var(--panel)" strokeWidth={2} />
        </>
      )}
    </svg>
    </div>
  );
}

function DayHour({ grid, tip }: { grid: number[][]; tip: ReturnType<typeof useTip> }) {
  const max = Math.max(1, ...grid.flat());
  const color = (v: number) => (v === 0 ? 'var(--ca-empty)' : RAMP[Math.min(RAMP.length - 1, Math.floor((v / max) * (RAMP.length - 1e-9)))]!);
  return (
    <div className="ca-heat" role="table" aria-label="Crimes by day of week and hour">
      <div className="ca-heat-row" role="row">
        <span role="columnheader" />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="ca-hh" role="columnheader">
            {h % 3 === 0 ? hourLabel(h) : ''}
          </span>
        ))}
      </div>
      {grid.map((row, d) => (
        <div key={d} className="ca-heat-row" role="row">
          <span className="ca-hd" role="rowheader">
            {DAYS[d]}
          </span>
          {row.map((v, h) => (
            <span
              key={h}
              role="cell"
              tabIndex={v ? 0 : -1}
              className="ca-cell"
              style={{ background: color(v) }}
              aria-label={`${DAYS[d]} ${hourLabel(h)}: ${v}`}
              onPointerMove={(e) => tip.show(e, [[String(v), `crime${v === 1 ? '' : 's'} · ${DAYS[d]} ${hourLabel(h)}–${hourLabel((h + 1) % 24)}`]])}
              onPointerLeave={tip.hide}
              onFocus={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                tip.show({ clientX: r.right, clientY: r.bottom }, [[String(v), `${DAYS[d]} ${hourLabel(h)}`]]);
              }}
              onBlur={tip.hide}
            />
          ))}
        </div>
      ))}
      <div className="ca-key">
        <span>Fewer</span>
        {RAMP.map((c) => (
          <i key={c} style={{ background: c }} />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}

export function CrimeAnalytics() {
  const [days, setDays] = useState(90);
  const [table, setTable] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/reports/analytics?days=${days}`), [days]);
  const tip = useTip();
  const { navigate } = useRouter();
  const base = location.pathname.startsWith('/field') ? '/field/reports' : '/reports';
  const d = r.data;
  const markers = useMemo(() => (d?.points ?? []).map((p: any) => ({ id: p.id, lat: p.lat, lng: p.lng, color: BLUE, title: p.code ?? 'Incident', dot: true })), [d]);
  const maxOff = Math.max(1, ...(d?.byOffense ?? []).map((x: any) => Math.max(x.count, x.previous)));
  const hasPrev = (d?.previous ?? 0) > 0;
  const short = (l: string) => l.replace(/\s*\([^)]*\)\s*$/, '');
  const maxCat = Math.max(1, ...(d?.byCategory ?? []).map((c: any) => c.count));
  return (
    <div className={`stack ca${r.loading && d ? ' ca-loading' : ''}`}>
      <PageHeader
        crumb={
          <button className="btn link" style={{ padding: 0, fontSize: 'inherit', color: 'inherit' }} onClick={() => navigate(base)}>
            Records
          </button>
        }
        title="Crime analytics"
        sub={`Approved reports only, compared with the ${days === 365 ? '12 months' : `${days} days`} before.`}
        actions={
          <div className="seg-ctl" role="group" aria-label="Period">
            {[
              [30, 'Last 30 days'],
              [90, 'Last 90 days'],
              [365, 'Last 12 months'],
            ].map(([n, l]) => (
              <button key={n} className={days === n ? 'on' : ''} aria-pressed={days === n} onClick={() => setDays(n as number)}>
                {l}
              </button>
            ))}
          </div>
        }
      />
      {r.error && <ErrorBox error={r.error} />}
      {!d ? (
        <Spinner />
      ) : (
        <>
          {d.notYetApproved > 0 && <p className="small muted">{d.notYetApproved} report{d.notYetApproved === 1 ? ' is' : 's are'} still being written or waiting for approval and aren’t counted yet.</p>}
          <div className="ca-stats">
            <Stat
              n={d.total}
              label="Crimes reported"
              sub={d.changePct == null ? 'no earlier data' : d.changePct === 0 ? 'same as before' : `${d.changePct > 0 ? '▲' : '▼'} ${Math.abs(d.changePct)}% vs ${d.previous} before`}
            />
            <Stat n={d.violent} label="Violent" />
            <Stat n={d.property} label="Property" />
            <Stat n={d.drugs} label="Drugs" />
            <Stat n={d.clearanceRate == null ? '—' : `${d.clearanceRate}%`} label="Cleared" sub={`${d.arrests} arrest${d.arrests === 1 ? '' : 's'}`} />
            <Stat n={d.domestic} label="Domestic" />
            <Stat n={d.firearm} label="Gun involved" />
            <Stat n={money(d.stolenValue)} label="Property stolen" />
          </div>

          {d.total === 0 ? (
            <p className="muted">No approved crime reports in this period yet.</p>
          ) : (
            <>
              <section className="card">
                <div className="row between">
                  <h2>Crimes per week</h2>
                  <button className="btn sm ghost" onClick={() => setTable(!table)}>
                    {table ? 'Show charts' : 'Show as a table'}
                  </button>
                </div>
                {table ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Week of</th>
                        <th>Crimes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.byWeek.map((w: any) => (
                        <tr key={w.weekStart}>
                          <td>{w.weekStart}</td>
                          <td>{w.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Trend weeks={d.byWeek} tip={tip} />
                )}
              </section>

              <div className="ca-two">
                <section className="card">
                  <h2>Offenses</h2>
                  <ul className="ca-bars">
                    {d.byOffense.slice(0, 10).map((x: any) => (
                      <li key={x.code} onPointerMove={(e) => tip.show(e, hasPrev ? [[String(x.count), `${x.label} this period`], [String(x.previous), 'the period before']] : [[String(x.count), x.label]])} onPointerLeave={tip.hide}>
                        <span className="ca-bl" title={x.label}>
                          {short(x.label)}
                        </span>
                        <span className="ca-track">
                          <span className="ca-bar" style={{ width: `${(x.count / maxOff) * 100}%` }} />
                          {hasPrev && <span className="ca-prev" style={{ left: `calc(${(x.previous / maxOff) * 100}% - 1px)` }} title="the period before" />}
                        </span>
                        <span className="ca-bv">
                          {x.count}
                          {hasPrev && x.previous !== x.count && <span className={`ca-delta ${x.count > x.previous ? 'up' : 'down'}`}>{x.count > x.previous ? '▲' : '▼'}{Math.abs(x.count - x.previous)}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {hasPrev && (
                    <p className="small muted ca-legend">
                      <i className="ca-k-bar" /> this period <i className="ca-k-prev" /> the period before
                    </p>
                  )}
                </section>
                <section className="card">
                  <h2>When crimes happen</h2>
                  {table ? (
                    <table className="table small">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Busiest hours</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.dayHour.map((row: number[], i: number) => (
                          <tr key={i}>
                            <td>{DAYS[i]}</td>
                            <td>
                              {row
                                .map((v, h) => [v, h] as const)
                                .filter(([v]) => v > 0)
                                .sort((a, b) => b[0] - a[0])
                                .slice(0, 3)
                                .map(([v, h]) => `${hourLabel(h)} (${v})`)
                                .join(', ') || '—'}
                            </td>
                            <td>{row.reduce((s, v) => s + v, 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <DayHour grid={d.dayHour} tip={tip} />
                  )}
                </section>
              </div>

              <div className="ca-two">
                <section className="card">
                  <h2>Where</h2>
                  <SlippyMap height={340} markers={markers} fitKey={`${days}|${markers.length}`} ariaLabel="Crime locations" onMarkerClick={(id) => navigate(`${base}/${id}`)} />
                </section>
                <section className="card">
                  <h2>Repeat locations</h2>
                  {d.hotSpots.length === 0 ? (
                    <p className="muted small">No address had more than one crime.</p>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Address</th>
                          <th>Crimes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.hotSpots.map((h: any) => (
                          <tr key={h.address}>
                            <td>{h.address}</td>
                            <td>{h.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <h3 className="ca-h3">By type</h3>
                  <ul className="ca-bars">
                    {d.byCategory.map((c: any) => (
                      <li key={c.category}>
                        <span className="ca-bl">{CATEGORY_LABELS[c.category as keyof typeof CATEGORY_LABELS] ?? c.category}</span>
                        <span className="ca-track">
                          <span className="ca-bar alt" style={{ width: `${(c.count / maxCat) * 100}%` }} />
                        </span>
                        <span className="ca-bv">{c.count}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </>
          )}
        </>
      )}
      {tip.el}
    </div>
  );
}
